
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Database, Copy, Filter, BarChart3 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const ProductDataExport = () => {
  const [format, setFormat] = useState("json");
  const [nameFilter, setNameFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [barcodeFilter, setBarcodeFilter] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [categories, setCategories] = useState<any[]>([]);
  const [productStats, setProductStats] = useState({ total: 0, withBarcode: 0, withCategory: 0, withPrice: 0 });
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  // ⚠️ SECURITY WARNING: In production, use environment variables or secure configuration
  // Example: const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  const supabaseUrl = "https://xdcjjtsakwthfuywzyjv.supabase.co";
  const supabaseKey = "YOUR_SUPABASE_ANON_KEY"; // Replace with actual key
  
  const apiEndpoint = `${supabaseUrl}/functions/v1/product-data-api`;

  // Load categories and stats on component mount
  useEffect(() => {
    const loadCategoriesAndStats = async () => {
      const { data: categoriesData } = await supabase
        .from('master_categories')
        .select('id, category_name')
        .eq('is_active', true)
        .order('category_name');
      
      if (categoriesData) {
        setCategories(categoriesData);
      }

      // Get product statistics using count queries to avoid row limits
      const [
        { count: totalCount },
        { count: barcodeCount },
        { count: categoryCount },
        { count: priceCount }
      ] = await Promise.all([
        supabase
          .from('master_products')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true),
        supabase
          .from('master_products')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true)
          .not('barcode', 'is', null),
        supabase
          .from('master_products')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true)
          .not('category_id', 'is', null),
        supabase
          .from('master_products')
          .select('*', { count: 'exact', head: true })
          .eq('is_active', true)
          .not('retail_price', 'is', null)
      ]);
      
      setProductStats({
        total: totalCount || 0,
        withBarcode: barcodeCount || 0,
        withCategory: categoryCount || 0,
        withPrice: priceCount || 0
      });
    };

    loadCategoriesAndStats();
  }, []);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      
      if (nameFilter) {
        params.append('name', nameFilter);
      }
      if (categoryFilter && categoryFilter !== "all") {
        params.append('category', categoryFilter);
      }
      if (barcodeFilter) {
        params.append('barcode', barcodeFilter);
      }
      if (minPrice) {
        params.append('min_price', minPrice);
      }
      if (maxPrice) {
        params.append('max_price', maxPrice);
      }

      const url = `${apiEndpoint}?${params.toString()}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${supabaseKey}`,
        }
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Create download
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `products.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      toast({
        title: "Export Complete",
        description: `Products exported successfully as ${format.toUpperCase()}`,
      });
    } catch (error: any) {
      toast({
        title: "Export Failed",
        description: error.message || "Failed to export product data",
        variant: "destructive"
      });
    } finally {
      setIsExporting(false);
    }
  };

  const copyApiUrl = () => {
    navigator.clipboard.writeText(apiEndpoint);
    toast({
      title: "API URL Copied",
      description: "Product data API endpoint copied to clipboard",
    });
  };

  const copyExampleCode = (language: string) => {
    let code = "";
    
    if (language === "javascript") {
      code = `// JavaScript example for accessing master product data
const apiUrl = '${apiEndpoint}';
const apiKey = 'YOUR_SUPABASE_ANON_KEY';

// Get all master products as JSON
async function getAllMasterProducts() {
  const response = await fetch(apiUrl, {
    headers: {
      'Authorization': \`Bearer \${apiKey}\`
    }
  });
  const data = await response.json();
  return data.data; // Array of master products with: product_name, barcode, category, retail_price
}

// Search by barcode
async function getProductByBarcode(barcode) {
  const response = await fetch(\`\${apiUrl}?barcode=\${barcode}\`, {
    headers: {
      'Authorization': \`Bearer \${apiKey}\`
    }
  });
  const data = await response.json();
  return data.data[0]; // First matching master product
}

// Filter by category and price range
async function getProductsByCategory(category, minPrice, maxPrice) {
  const params = new URLSearchParams({
    category: category,
    min_price: minPrice.toString(),
    max_price: maxPrice.toString()
  });
  const response = await fetch(\`\${apiUrl}?\${params}\`, {
    headers: {
      'Authorization': \`Bearer \${apiKey}\`
    }
  });
  const data = await response.json();
  return data.data; // Filtered master products
}

// Get products as CSV
async function getMasterProductsCSV() {
  const response = await fetch(\`\${apiUrl}?format=csv\`, {
    headers: {
      'Authorization': \`Bearer \${apiKey}\`
    }
  });
  return await response.text(); // CSV string with: serial_number, barcode, product_name, category, retail_price
}`;
    } else if (language === "pascal") {
      code = `// Pascal/Delphi example for accessing product data
uses
  IdHTTP, IdSSLOpenSSL, System.JSON;

const
  API_URL = '${apiEndpoint}';
  API_KEY = 'YOUR_SUPABASE_ANON_KEY'; // ⚠️ Replace with your actual key

function GetAllProducts: TJSONArray;
var
  HTTP: TIdHTTP;
  SSL: TIdSSLIOHandlerSocketOpenSSL;
  Response: string;
  JSONObj: TJSONObject;
begin
  Result := nil;
  HTTP := TIdHTTP.Create;
  SSL := TIdSSLIOHandlerSocketOpenSSL.Create;
  try
    HTTP.IOHandler := SSL;
    HTTP.Request.CustomHeaders.Values['Authorization'] := 'Bearer ' + API_KEY;
    
    Response := HTTP.Get(API_URL);
    JSONObj := TJSONObject.ParseJSONValue(Response) as TJSONObject;
    
    if Assigned(JSONObj) then
      Result := JSONObj.GetValue('data') as TJSONArray;
  finally
    HTTP.Free;
    SSL.Free;
  end;
end;

function GetProductByBarcode(const Barcode: string): TJSONObject;
var
  HTTP: TIdHTTP;
  SSL: TIdSSLIOHandlerSocketOpenSSL;
  Response: string;
  JSONObj: TJSONObject;
  DataArray: TJSONArray;
begin
  Result := nil;
  HTTP := TIdHTTP.Create;
  SSL := TIdSSLIOHandlerSocketOpenSSL.Create;
  try
    HTTP.IOHandler := SSL;
    HTTP.Request.CustomHeaders.Values['Authorization'] := 'Bearer ' + API_KEY;
    
    Response := HTTP.Get(API_URL + '?barcode=' + Barcode);
    JSONObj := TJSONObject.ParseJSONValue(Response) as TJSONObject;
    
    if Assigned(JSONObj) then
    begin
      DataArray := JSONObj.GetValue('data') as TJSONArray;
      if (DataArray.Count > 0) then
        Result := DataArray.Items[0] as TJSONObject;
    end;
  finally
    HTTP.Free;
    SSL.Free;
  end;
end;`;
    }

    navigator.clipboard.writeText(code);
    toast({
      title: "Code Copied",
      description: `${language} example code copied to clipboard`,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-600" />
          Product Data Export & API
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Product Statistics */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            <h3 className="font-medium text-gray-900">Master Products Overview</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{productStats.total}</div>
              <div className="text-gray-600">Total Products</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{productStats.withBarcode}</div>
              <div className="text-gray-600">With Barcode</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{productStats.withCategory}</div>
              <div className="text-gray-600">Categorized</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{productStats.withPrice}</div>
              <div className="text-gray-600">With Price</div>
            </div>
          </div>
        </div>

        {/* Export Section */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="font-medium text-gray-900">Export Master Products</h3>
          </div>
          
          {/* Advanced Filtering */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <Label htmlFor="nameFilter">Product Name</Label>
              <Input
                id="nameFilter"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Search by name..."
              />
            </div>
            
            <div>
              <Label htmlFor="categoryFilter">Category</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.category_name}>
                      {cat.category_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="barcodeFilter">Barcode</Label>
              <Input
                id="barcodeFilter"
                value={barcodeFilter}
                onChange={(e) => setBarcodeFilter(e.target.value)}
                placeholder="Exact barcode..."
              />
            </div>

            <div>
              <Label htmlFor="minPrice">Min Price</Label>
              <Input
                id="minPrice"
                type="number"
                step="0.01"
                value={minPrice}
                onChange={(e) => setMinPrice(e.target.value)}
                placeholder="0.00"
              />
            </div>

            <div>
              <Label htmlFor="maxPrice">Max Price</Label>
              <Input
                id="maxPrice"
                type="number"
                step="0.01"
                value={maxPrice}
                onChange={(e) => setMaxPrice(e.target.value)}
                placeholder="999.99"
              />
            </div>

            <div>
              <Label htmlFor="format">Export Format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="json">JSON</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xml">XML</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Export includes: Product Name, Category, Retail Price, Barcode
            </div>
            <Button 
              onClick={handleExport}
              disabled={isExporting}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {isExporting ? 'Exporting...' : 'Export Data'}
            </Button>
          </div>
        </div>

        {/* API Information */}
        <div className="space-y-4 pt-6 border-t">
          <h3 className="font-medium text-gray-900">API Endpoint for Label Printing App</h3>
          
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-sm text-gray-700">API Endpoint:</span>
              <Button
                variant="outline"
                size="sm"
                onClick={copyApiUrl}
                className="flex items-center gap-1"
              >
                <Copy className="w-3 h-3" />
                Copy
              </Button>
            </div>
            <code className="text-xs text-gray-600 break-all">{apiEndpoint}</code>
          </div>

          {/* API Usage Examples */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-gray-800">Available Query Parameters (Master Products):</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <div><code>?format=json|csv|xml</code> - Response format</div>
              <div><code>?barcode=12345</code> - Search by exact barcode</div>
              <div><code>?name=product</code> - Search by product name (partial match)</div>
              <div><code>?category=Electronics</code> - Filter by category name</div>
              <div><code>?category_id=uuid</code> - Filter by category ID</div>
              <div><code>?min_price=10&max_price=100</code> - Price range filtering</div>
              <div><code>?has_barcode=true|false</code> - Filter by barcode presence</div>
              <div><code>?active=true|false</code> - Active/inactive products (default: true)</div>
              <div><code>?limit=100&offset=0</code> - Pagination</div>
              <div><code>?sort_by=product_name&sort_order=asc</code> - Sorting</div>
            </div>
          </div>

          {/* Code Examples */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-gray-800">Integration Examples:</h4>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyExampleCode("javascript")}
                className="flex items-center gap-1"
              >
                <Copy className="w-3 h-3" />
                JavaScript Example
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyExampleCode("pascal")}
                className="flex items-center gap-1"
              >
                <Copy className="w-3 h-3" />
                Pascal Example
              </Button>
            </div>
          </div>

          {/* Authentication Note */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-sm text-yellow-800">
              <strong>Authentication:</strong> Include your Supabase anon key in the Authorization header: 
              <code className="ml-1 bg-yellow-100 px-1 rounded">Bearer YOUR_SUPABASE_ANON_KEY</code>
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              ⚠️ Replace YOUR_SUPABASE_ANON_KEY with your actual anon key from Supabase settings
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
